<template>
  <AppLayout page-title="My Payroll" page-subtitle="Earnings, payments, and tax withholding">
    <div class="pt-8">
      <!-- Page Header -->
      <div class="mb-8 px-8">
        <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
          <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
          My Payroll
        </div>
        <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">My Payroll</h1>
        <p class="text-xl text-slate-300 mb-8 max-w-2xl">Track your earnings, payment history, and tax information</p>
      </div>

      <!-- Payroll Overview Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 px-8 mb-8">
        <div class="payroll-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/money and dollar.png" alt="Total Earnings" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Earnings</p>
            <p class="text-3xl font-bold text-white">${{ money(payrollStats.totalEarnings) }}</p>
            <p class="text-sm text-emerald-400">This year</p>
          </div>
        </div>

        <div class="payroll-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/hand holding dollar coin.png" alt="This Month" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">This Month</p>
            <p class="text-3xl font-bold text-white">${{ money(payrollStats.monthlyEarnings) }}</p>
            <p class="text-sm text-emerald-400">{{ payrollStats.monthlyHours }} hours</p>
          </div>
        </div>

        <div class="payroll-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/gold dollar coin.png" alt="Hourly Rate" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Hourly Rate</p>
            <p class="text-3xl font-bold text-white">${{ payrollStats.hourlyRate }}</p>
            <p class="text-sm text-emerald-400">Current rate</p>
          </div>
        </div>

        <div class="payroll-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-slate-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/bank office icon.png" alt="Next Payment" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Next Payment</p>
            <p class="text-3xl font-bold text-white">${{ money(payrollStats.nextPayment) }}</p>
            <p class="text-sm text-emerald-400">{{ payrollStats.nextPaymentDate }}</p>
          </div>
        </div>
      </div>

      <!-- Recent Payments -->
      <div class="px-8 mb-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-bold text-white">Recent Payments</h2>
          <button @click="exportPayrollData" class="btn-outline-modern">
            <img src="/src/assets/images/icons/gear icon with neon.png" alt="Export" class="w-4 h-4 mr-2">
            Export Data
          </button>
        </div>

        <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/30">
                <tr>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Date</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Company</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Hours</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Rate</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Amount</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-if="!recentPayments.length">
                  <td colspan="7" class="px-6 py-10 text-center text-sm text-slate-400">No payroll payments yet.</td>
                </tr>
                <tr v-for="payment in recentPayments" :key="payment.id" class="hover:bg-slate-700/20 transition-colors">
                  <td class="px-6 py-4 text-sm text-slate-300">{{ formatDate(payment.date) }}</td>
                  <td class="px-6 py-4 text-sm text-white font-medium">{{ payment.company }}</td>
                  <td class="px-6 py-4 text-sm text-slate-300">{{ payment.hours }}h</td>
                  <td class="px-6 py-4 text-sm text-slate-300">${{ payment.rate }}/hr</td>
                  <td class="px-6 py-4 text-sm text-emerald-400 font-semibold">${{ money(payment.amount) }}</td>
                  <td class="px-6 py-4">
                    <span :class="getStatusClass(payment.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                      {{ payment.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <button @click="viewPaymentDetails(payment)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Tax Information -->
      <div class="px-8 mb-8">
        <h2 class="text-2xl font-bold text-white mb-6">Tax Information</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6">
            <div class="flex items-center mb-4">
              <img src="/src/assets/images/icons/gold dollar coin.png" alt="Tax YTD" class="w-8 h-8 mr-3">
              <h3 class="text-lg font-semibold text-white">YTD Earnings</h3>
            </div>
            <p class="text-3xl font-bold text-emerald-400 mb-2">${{ money(taxInfo.ytdEarnings) }}</p>
            <p class="text-sm text-slate-400">Year to date</p>
          </div>

          <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6">
            <div class="flex items-center mb-4">
              <img src="/src/assets/images/icons/ai-powered financial research tools.png" alt="Tax Withheld" class="w-8 h-8 mr-3">
              <h3 class="text-lg font-semibold text-white">Tax Withheld</h3>
            </div>
            <p class="text-3xl font-bold text-amber-400 mb-2">${{ money(taxInfo.taxWithheld) }}</p>
            <p class="text-sm text-slate-400">Total withheld</p>
          </div>

          <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6">
            <div class="flex items-center mb-4">
              <img src="/src/assets/images/icons/bank office icon.png" alt="Net Pay" class="w-8 h-8 mr-3">
              <h3 class="text-lg font-semibold text-white">Net Pay</h3>
            </div>
            <p class="text-3xl font-bold text-cyan-400 mb-2">${{ money(taxInfo.netPay) }}</p>
            <p class="text-sm text-slate-400">After taxes</p>
          </div>
        </div>
      </div>

      <!-- Payment Schedule -->
      <div class="px-8 mb-8">
        <h2 class="text-2xl font-bold text-white mb-6">Payment Schedule</h2>
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 class="text-lg font-semibold text-white mb-4">Upcoming Payments</h3>
              <div class="space-y-4">
                <p v-if="!paymentSchedule.length" class="text-sm text-slate-400">No upcoming payments scheduled.</p>
                <div v-for="schedule in paymentSchedule" :key="schedule.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                  <div>
                    <p class="text-white font-medium">{{ schedule.company }}</p>
                    <p class="text-sm text-slate-400">{{ formatDate(schedule.date) }}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-emerald-400 font-semibold">${{ money(schedule.amount) }}</p>
                    <p class="text-sm text-slate-400">{{ schedule.hours }} hours</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white mb-4">Payment Preferences</h3>
              <div class="space-y-4">
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Payment Method</p>
                  <p class="text-slate-400">{{ paymentPreferences.method }}</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Payment Frequency</p>
                  <p class="text-slate-400">{{ paymentPreferences.frequency }}</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Tax Withholding</p>
                  <p class="text-slate-400">{{ paymentPreferences.taxWithholding }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div class="px-8 mb-8 text-center">
        <button @click="loadMorePayments" class="btn-outline-modern" :disabled="isLoading">
          <span v-if="isLoading">Loading...</span>
          <span v-else>Load More Payments</span>
        </button>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import { onMounted, reactive, ref } from 'vue'
import { useUserStore } from '../stores/userStore'

// Store
const userStore = useUserStore()

// State
const isLoading = ref(false)

// Payroll Statistics
const payrollStats = reactive({
  totalEarnings: 0,
  monthlyEarnings: 0,
  monthlyHours: 0,
  hourlyRate: 0,
  nextPayment: 0,
  nextPaymentDate: ''
})

// Recent Payments
const recentPayments = ref<any[]>([])

// Tax Information
const taxInfo = reactive({
  ytdEarnings: 0,
  taxWithheld: 0,
  netPay: 0
})

// Payment Schedule
const paymentSchedule = ref<any[]>([])

// Payment Preferences
const paymentPreferences = reactive({
  method: 'Direct Deposit',
  frequency: 'Weekly',
  taxWithholding: 'Standard Rate'
})



function money(value: unknown) {
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString() : '0'
}

function num(value: unknown) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function authHeaders() {
  const token = userStore.authToken || localStorage.getItem('auth_token') || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }
}

async function readJson(response: Response) {
  const text = await response.text()
  if (!response.ok) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function mapPayment(row: Record<string, any>, index: number) {
  const hours = num(row.hours ?? row.regular_hours) + num(row.overtime_hours)
  return {
    id: row.id ?? index,
    date: row.date || row.pay_date || '',
    company: row.company || row.company_name || 'Payroll',
    hours,
    rate: num(row.rate ?? row.hourly_rate),
    amount: num(row.amount ?? row.net_pay ?? row.gross_pay),
    status: String(row.status || 'Paid')
  }
}

function mapSchedule(row: Record<string, any>, index: number) {
  return {
    id: row.id ?? index,
    company: row.company || row.company_name || 'Upcoming pay',
    date: row.date || row.pay_date || '',
    amount: num(row.amount ?? row.estimated_net_pay ?? row.estimated_gross_pay),
    hours: num(row.hours ?? row.hours_per_week)
  }
}

const fetchPayrollData = async () => {
  try {
    isLoading.value = true

    const [stats, paymentsPayload, tax, schedule] = await Promise.all([
      fetch('/api/payroll/stats', { headers: authHeaders() }).then(readJson),
      fetch('/api/payroll/payments', { headers: authHeaders() }).then(readJson),
      fetch('/api/payroll/tax-info', { headers: authHeaders() }).then(readJson),
      fetch('/api/payroll/schedule', { headers: authHeaders() }).then(readJson)
    ])

    if (!stats && !paymentsPayload && !tax && !schedule) {
      loadMockData()
      return
    }

    const ytd = stats?.year_to_date || {}
    const month = stats?.current_month || {}
    const payRows = Array.isArray(paymentsPayload)
      ? paymentsPayload
      : (Array.isArray(paymentsPayload?.payments) ? paymentsPayload.payments : [])
    recentPayments.value = payRows.map(mapPayment)

    const upcoming = Array.isArray(schedule)
      ? schedule
      : (Array.isArray(schedule?.upcoming_pay_dates) ? schedule.upcoming_pay_dates : [])
    paymentSchedule.value = upcoming.map(mapSchedule)

    const latest = recentPayments.value[0]
    const next = paymentSchedule.value[0]
    const payPref = schedule?.pay_schedule || {}

    Object.assign(payrollStats, {
      totalEarnings: num(stats?.totalEarnings ?? ytd.gross_pay ?? ytd.ytd_gross_pay),
      monthlyEarnings: num(stats?.monthlyEarnings ?? month.gross_pay ?? month.total_gross_pay),
      monthlyHours: num(stats?.monthlyHours ?? month.overtime_hours) || recentPayments.value.reduce((sum, p) => sum + num(p.hours), 0),
      hourlyRate: num(stats?.hourlyRate ?? latest?.rate ?? payPref.hourly_rate),
      nextPayment: num(stats?.nextPayment ?? next?.amount),
      nextPaymentDate: stats?.nextPaymentDate || (next?.date ? formatDate(next.date) : (payPref.next_pay_date ? formatDate(payPref.next_pay_date) : '—'))
    })

    const summary = tax?.summary || {}
    Object.assign(taxInfo, {
      ytdEarnings: num(tax?.ytdEarnings ?? summary.total_gross_pay),
      taxWithheld: num(tax?.taxWithheld ?? summary.total_tax_withheld),
      netPay: num(tax?.netPay ?? summary.total_net_pay)
    })

    if (payPref.pay_frequency) {
      paymentPreferences.frequency = String(payPref.pay_frequency).replace(/^\w/, (c: string) => c.toUpperCase())
    }
  } catch (error) {
    console.error('Error fetching payroll data:', error)
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  // Mock payroll statistics
  Object.assign(payrollStats, {
    totalEarnings: 45600,
    monthlyEarnings: 8200,
    monthlyHours: 160,
    hourlyRate: 45,
    nextPayment: 3200,
    nextPaymentDate: 'Dec 15, 2024'
  })

  // Mock recent payments
  recentPayments.value = [
    {
      id: 1,
      date: '2024-12-01',
      company: 'ExxonMobil',
      hours: 40,
      rate: 45,
      amount: 1800,
      status: 'Paid'
    },
    {
      id: 2,
      date: '2024-11-24',
      company: 'Chevron',
      hours: 38,
      rate: 42,
      amount: 1596,
      status: 'Paid'
    },
    {
      id: 3,
      date: '2024-11-17',
      company: 'BP',
      hours: 42,
      rate: 48,
      amount: 2016,
      status: 'Paid'
    },
    {
      id: 4,
      date: '2024-11-10',
      company: 'Shell',
      hours: 36,
      rate: 44,
      amount: 1584,
      status: 'Processing'
    }
  ]

  // Mock tax information
  Object.assign(taxInfo, {
    ytdEarnings: 45600,
    taxWithheld: 9120,
    netPay: 36480
  })

  // Mock payment schedule
  paymentSchedule.value = [
    {
      id: 1,
      company: 'ExxonMobil',
      date: '2024-12-15',
      amount: 3200,
      hours: 40
    },
    {
      id: 2,
      company: 'Chevron',
      date: '2024-12-22',
      amount: 2800,
      hours: 35
    }
  ]
}

const formatDate = (dateString: string) => {
  if (!dateString) return '—'
  const parsed = new Date(dateString)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getStatusClass = (status: string) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid':
      return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'processing':
      return 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
    case 'pending':
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
    default:
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
}

const viewPaymentDetails = (payment: any) => {
  // Navigate to payment details page or show modal
  console.log('View payment details:', payment)
}

const exportPayrollData = () => {
  // Export payroll data to CSV
  const csvContent = generateCSV()
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `payroll-data-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  window.URL.revokeObjectURL(url)
}

const generateCSV = () => {
  const headers = ['Date', 'Company', 'Hours', 'Rate', 'Amount', 'Status']
  const rows = recentPayments.value.map(payment => [
    formatDate(payment.date),
    payment.company,
    payment.hours,
    `$${payment.rate}`,
    `$${payment.amount}`,
    payment.status
  ])
  
  return [headers, ...rows].map(row => row.join(',')).join('\n')
}

const loadMorePayments = async () => {
  // Load more payment history
  console.log('Loading more payments...')
}

// Lifecycle
onMounted(() => {
  fetchPayrollData()
})
</script>

<style scoped>
.payroll-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6 transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-600/50;
}

.btn-primary-modern {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-200 shadow-lg shadow-emerald-500/25;
}

.btn-outline-modern {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 font-medium rounded-xl hover:bg-slate-700/50 hover:border-slate-500 transition-all duration-200;
}
</style>
